import React from "react";
import { HiArrowRight } from "react-icons/hi";
import { Link } from "react-router-dom";
import { usePublicPageSection } from "../../../../hooks/usePublicPageSection";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";
import { useLanguage } from "../../../../context/LanguageContext";
import "./WhatWeDo.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`What we do field "${key}" is missing from the API response.`);
  }
  return value;
};

const requireMediaUrl = (section, groupKey) => {
  const item = section.media?.groups?.[groupKey]?.[0] || null;
  if (!item?.url) {
    throw new Error(`What we do media group "${groupKey}" is missing from the API response.`);
  }
  return resolveMediaUrl(item.url);
};

const buildModel = (section) => {
  const f = section.fields || {};
  return {
    badge: requireField(f, "badge"),
    title: requireField(f, "title"),
    subtitle: requireField(f, "subtitle"),
    linkText: requireField(f, "linkText"),
    linkUrl: requireField(f, "linkUrl"),
    cards: [
      {
        title: requireField(f, "card1Title"),
        text: requireField(f, "card1Text"),
        icon: requireMediaUrl(section, "card-1"),
      },
      {
        title: requireField(f, "card2Title"),
        text: requireField(f, "card2Text"),
        icon: requireMediaUrl(section, "card-2"),
      },
      {
        title: requireField(f, "card3Title"),
        text: requireField(f, "card3Text"),
        icon: requireMediaUrl(section, "card-3"),
      },
      {
        title: requireField(f, "card4Title"),
        text: requireField(f, "card4Text"),
        icon: requireMediaUrl(section, "card-4"),
      },
    ],
  };
};

const isExternal = (href) => /^https?:\/\//i.test(href);

const MandateLink = ({ href, className, children }) => {
  if (isExternal(href)) {
    return (
      <a href={href} className={className} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  if (href.startsWith("#") || href.includes("/#")) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
};

const WhatWeDo = () => {
  const { meta } = useLanguage();
  const { model, error, isLoading } = usePublicPageSection(
    "about",
    "what-we-do",
    buildModel
  );

  if (isLoading) {
    return (
      <section className="what-we-do" dir={meta.dir}>
        <div className="what-we-do__container">
          <p style={{ color: "#64748b", fontSize: 14 }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (error || !model) {
    return (
      <section className="what-we-do" dir={meta.dir}>
        <div className="what-we-do__container">
          <p style={{ color: "#b91c1c", fontSize: 14 }}>
            {error || "What we do unavailable."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="what-we-do" dir={meta.dir}>
      <div className="what-we-do__container">
        <div className="what-we-do__header">
          <span className="what-we-do__badge">{model.badge}</span>
          <h2 className="what-we-do__title">{model.title}</h2>
          <p className="what-we-do__subtitle">{model.subtitle}</p>
        </div>

        <div className="what-we-do__cards">
          {model.cards.map((item) => (
            <article key={item.title} className="what-we-do__card">
              <div className="what-we-do__card-left">
                <div className="what-we-do__card-icon">
                  <img src={item.icon} alt="" aria-hidden="true" />
                </div>
                <h3 className="what-we-do__card-title">{item.title}</h3>
              </div>
              <p className="what-we-do__card-text">{item.text}</p>
            </article>
          ))}
        </div>

        <MandateLink href={model.linkUrl} className="what-we-do__link">
          <span>{model.linkText}</span>
          <HiArrowRight />
        </MandateLink>
      </div>
    </section>
  );
};

export default WhatWeDo;
